/**
 * Amount Formatting Utilities
 * 
 * FORMATTING RULES:
 * - Main (Sale Deed): No decimals - ₹50,00,000
 * - Receivables (Hidden/Charges/Terrace/Other/Banakhat/Expense/Partner): 2 decimals - ₹1,00,000.00
 * - Totals (if includes ANY Receivables): 2 decimals - ₹52,50,000.00
 */

/**
 * Format Main amounts (Sale Deed Amount, Main Income)
 * No decimals, Indian currency format
 * 
 * @param amount - Number to format
 * @returns Formatted string like "₹50,00,000"
 * 
 * @example
 * formatMainDBAmount(5000000)     // "₹50,00,000"
 * formatMainDBAmount(6100000)     // "₹61,00,000"
 * formatMainDBAmount(1234567.89)  // "₹12,34,568" (rounded)
 */
export const formatMainDBAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '₹0';
  }
  
  return `₹${Math.round(amount).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
};

/**
 * Format Receivables amounts (Hidden Income, Charges, Terrace, Other Work, 
 * Banakhat, Expenses, Partner Income, Other Income)
 * Always 2 decimals, Indian currency format
 * 
 * @param amount - Number to format
 * @returns Formatted string like "₹1,00,000.00"
 * 
 * @example
 * formatSystemsDBAmount(100000)      // "₹1,00,000.00"
 * formatSystemsDBAmount(50000.50)    // "₹50,000.50"
 * formatSystemsDBAmount(1234567.89)  // "₹12,34,567.89"
 * formatSystemsDBAmount(5000)        // "₹5,000.00"
 */
export const formatSystemsDBAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '₹0.00';
  }
  
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Format total amount - use when combining Main + Receivables amounts
 * Always 2 decimals for consistency
 * 
 * @param amount - Number to format
 * @returns Formatted string like "₹52,50,000.00"
 * 
 * @example
 * // Booking total = Sale Deed + Charges + Terrace + Other + Banakhat
 * const total = 5000000 + 100000 + 50000 + 75000 + 25000;
 * formatTotalAmount(total)  // "₹52,50,000.00"
 */
export const formatTotalAmount = (amount: number | null | undefined): string => {
  return formatSystemsDBAmount(amount);
};

/**
 * Parse Main input value to number (no decimals)
 * Rounds to nearest integer
 * 
 * @param value - Input string
 * @returns Rounded integer
 * 
 * @example
 * parseMainDBInput("5000000")    // 5000000
 * parseMainDBInput("5000000.99") // 5000001
 * parseMainDBInput("abc")        // 0
 */
export const parseMainDBInput = (value: string | number): number => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Math.round(parsed) || 0;
};

/**
 * Parse Receivables input value to number (2 decimals)
 * Rounds to 2 decimal places
 * 
 * @param value - Input string
 * @returns Number with 2 decimals
 * 
 * @example
 * parseSystemsDBInput("100000.50")   // 100000.50
 * parseSystemsDBInput("100000.999")  // 100001.00
 * parseSystemsDBInput("abc")         // 0
 */
export const parseSystemsDBInput = (value: string | number): number => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(parsed)) return 0;
  return parseFloat(parsed.toFixed(2));
};

/**
 * Determine which format to use based on amount type
 * 
 * @param amount - Number to format
 * @param type - Type of amount ('main' | 'systems' | 'total')
 * @returns Formatted string
 * 
 * @example
 * formatAmount(5000000, 'main')     // "₹50,00,000"
 * formatAmount(100000, 'systems')   // "₹1,00,000.00"
 * formatAmount(5250000, 'total')    // "₹52,50,000.00"
 */
export const formatAmount = (
  amount: number | null | undefined,
  type: 'main' | 'systems' | 'total' = 'systems'
): string => {
  switch (type) {
    case 'main':
      return formatMainDBAmount(amount);
    case 'systems':
      return formatSystemsDBAmount(amount);
    case 'total':
      return formatTotalAmount(amount);
    default:
      return formatSystemsDBAmount(amount);
  }
};

/**
 * Calculate and format booking total amount
 * Includes Sale Deed + all hidden amounts with proper formatting
 * 
 * @param booking - Booking object with amounts
 * @returns Formatted total with 2 decimals
 * 
 * @example
 * const booking = {
 *   saleDeedAmount: 5000000,
 *   chargesAmount: 100000,
 *   terraceAmount: 50000,
 *   otherWorkAmount: 75000,
 *   banakhatAmount: 25000
 * };
 * formatBookingTotal(booking)  // "₹52,50,000.00"
 */
export const formatBookingTotal = (booking: {
  saleDeedAmount?: number | null;
  chargesAmount?: number | null;
  terraceAmount?: number | null;
  otherWorkAmount?: number | null;
  banakhatAmount?: number | null;
}): string => {
  const total = 
    (booking.saleDeedAmount || 0) +
    (booking.chargesAmount || 0) +
    (booking.terraceAmount || 0) +
    (booking.otherWorkAmount || 0) +
    (booking.banakhatAmount || 0);
  
  return formatTotalAmount(total);
};

/**
 * Format currency for display in tables (right-aligned)
 * Returns object with formatted value and style
 * 
 * @param amount - Number to format
 * @param type - Type of amount
 * @param isNegative - Whether to show in red
 * @returns Object with value and style
 * 
 * @example
 * const { value, style } = formatCurrencyDisplay(100000, 'systems', true);
 * // value: "₹1,00,000.00"
 * // style: { textAlign: 'right', color: '#dc2626' }
 */
export const formatCurrencyDisplay = (
  amount: number | null | undefined,
  type: 'main' | 'systems' | 'total' = 'systems',
  isNegative?: boolean
) => {
  const value = formatAmount(amount, type);
  const actualAmount = amount || 0;
  const shouldShowRed = isNegative !== undefined ? isNegative : actualAmount < 0;
  
  return {
    value,
    style: {
      textAlign: 'right' as const,
      color: shouldShowRed ? '#dc2626' : 'inherit'
    }
  };
};

/**
 * Validate Main amount input (no decimals allowed)
 * 
 * @param value - Input value
 * @returns Error message or null
 */
export const validateMainDBAmount = (value: string | number): string | null => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(parsed)) {
    return 'Please enter a valid amount';
  }
  
  if (parsed < 0) {
    return 'Amount cannot be negative';
  }
  
  // Check if has decimals
  const hasDecimals = typeof value === 'string' ? value.includes('.') : false;
  if (hasDecimals && parsed !== Math.floor(parsed)) {
    return 'Sale Deed amount should not have decimals';
  }
  
  return null;
};

/**
 * Validate Receivables amount input (2 decimals max)
 * 
 * @param value - Input value
 * @returns Error message or null
 */
export const validateSystemsDBAmount = (value: string | number): string | null => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(parsed)) {
    return 'Please enter a valid amount';
  }
  
  if (parsed < 0) {
    return 'Amount cannot be negative (use negative sign for refunds)';
  }
  
  // Check decimal places
  if (typeof value === 'string') {
    const decimalPart = value.split('.')[1];
    if (decimalPart && decimalPart.length > 2) {
      return 'Amount can have maximum 2 decimal places';
    }
  }
  
  return null;
};

// Export type definitions for TypeScript
export type AmountType = 'main' | 'systems' | 'total';

export interface FormattedCurrency {
  value: string;
  style: {
    textAlign: 'right';
    color: string;
  };
}
